import React, { useRef, useState } from 'react';

interface ThumbnailPickerProps {
  videoUrl: string;
  currentThumbnail: string | null;
  onThumbnailChange: (thumbnailUrl: string) => void;
}

export const ThumbnailPicker: React.FC<ThumbnailPickerProps> = ({
  videoUrl,
  currentThumbnail,
  onThumbnailChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isExtracting, setIsExtracting] = useState(false);

  const extractFrameFromVideo = () => {
    setIsExtracting(true);

    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    video.crossOrigin = 'anonymous';

    video.addEventListener('loadeddata', () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const seekTime = Math.min(1, video.duration * 0.1);
      video.currentTime = seekTime;
    });

    video.addEventListener('seeked', () => {
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
        onThumbnailChange(thumbnail);
      }
      setIsExtracting(false);
      video.remove();
    });

    video.addEventListener('error', () => {
      setIsExtracting(false);
      video.remove();
    });

    video.src = videoUrl;
    video.load();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      if (result) {
        onThumbnailChange(result);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-[#2f4941]/70">
        Thumbnail
      </label>

      {/* Thumbnail preview */}
      {currentThumbnail && (
        <div className="relative w-full rounded-lg overflow-hidden bg-surface-ui" style={{ aspectRatio: '16/9' }}>
          <img
            src={currentThumbnail}
            alt="Video thumbnail"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={extractFrameFromVideo}
          disabled={isExtracting}
          className="flex-1 px-3 py-1.5 text-xs bg-[#2f4941]/10 text-[#2f4941] rounded-lg hover:bg-[#2f4941]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
        >
          {isExtracting ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#2f4941]"></div>
              Extracting...
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Auto-extract
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Upload custom
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default ThumbnailPicker;
